import React from 'react';
import { Badge } from 'reactstrap';
import { formatAlphaPrice } from '../../../../../../utils/deliveryDetailUtils';

const hasFilledValue = value =>
  value != null && String(value).trim() !== '' && String(value).trim() !== '-';

const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (hasFilledValue(value)) return value;
  }
  return null;
};

export const MATCHING_HPS_LABEL = {
  unavailable: 'HPS tidak tersedia',
  lower: 'Lebih Rendah dari HPS',
  higher: 'Lebih Tinggi dari HPS',
};

export const parseAlphaNumber = value => {
  if (!hasFilledValue(value)) return null;
  const numeric = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
};

// alphaDyedHps/alphaUndyedHps adalah nama yang benar-benar dikirim BE
// (get-delivery-detail & get-delivery-parcel, level listData maupun object lokasi).
export const getHpsAlphaValues = row => ({
  alpha: pickFirstFilled(row?.hpsAlpha, row?.alphaHps, row?.HpsAlpha),
  dyed: pickFirstFilled(
    row?.hpsAlphaDyed,
    row?.hpsDyed,
    row?.alphaHpsDyed,
    row?.HpsAlphaDyed,
    row?.alphaDyedHps,
    row?.AlphaDyedHps
  ),
  undyed: pickFirstFilled(
    row?.hpsAlphaUndyed,
    row?.hpsUndyed,
    row?.alphaHpsUndyed,
    row?.HpsAlphaUndyed,
    row?.alphaUndyedHps,
    row?.AlphaUndyedHps
  ),
});

export const resolveMatchingHpsKey = ({
  usesDyedUndyed,
  offerAlpha,
  offerDyed,
  offerUndyed,
  hps,
  statusFromApi,
  hpsAvailable = true,
}) => {
  if (!hpsAvailable) return 'unavailable';

  const raw = String(
    statusFromApi ?? hps?.matchingHps ?? hps?.hpsMatching ?? ''
  )
    .trim()
    .toLowerCase();
  if (raw.includes('tinggi') || raw === 'higher') return 'higher';
  if (raw.includes('rendah') || raw === 'lower') return 'lower';
  if (raw.includes('tidak tersedia') || raw === 'unavailable') {
    return 'unavailable';
  }

  const pairs = usesDyedUndyed
    ? [
        [parseAlphaNumber(offerDyed), parseAlphaNumber(hps.dyed)],
        [parseAlphaNumber(offerUndyed), parseAlphaNumber(hps.undyed)],
      ]
    : [[parseAlphaNumber(offerAlpha), parseAlphaNumber(hps.alpha)]];

  const comparable = pairs.filter(([, hpsValue]) => hpsValue != null);
  if (!comparable.length) return 'unavailable';
  if (
    comparable.some(([offer, hpsValue]) => offer != null && offer > hpsValue)
  ) {
    return 'higher';
  }
  if (
    comparable.some(([offer, hpsValue]) => offer != null && offer < hpsValue)
  ) {
    return 'lower';
  }
  return 'unavailable';
};

export const MatchingHpsBadge = ({ matchKey }) => {
  const color =
    matchKey === 'lower'
      ? 'success'
      : matchKey === 'higher'
        ? 'danger'
        : 'secondary';
  return (
    <Badge
      color={color}
      className='px-2 py-1 fs-11 fw-medium rounded-pill text-nowrap'
    >
      {MATCHING_HPS_LABEL[matchKey] ?? MATCHING_HPS_LABEL.unavailable}
    </Badge>
  );
};

const AlphaPriceText = ({ value, suffix }) => (
  <span className='text-nowrap'>{formatAlphaPrice(value, suffix)}</span>
);

export const HpsAlphaCell = ({ usesDyedUndyed, hps, suffix, hpsAvailable }) => {
  if (!hpsAvailable) {
    return <span className='text-muted'>-</span>;
  }

  if (usesDyedUndyed) {
    return (
      <div className='d-flex flex-column gap-0 lh-sm fs-12'>
        <span className='text-nowrap'>
          Dyed <AlphaPriceText value={hps.dyed} suffix={suffix} />
        </span>
        <span className='text-nowrap'>
          Undyed <AlphaPriceText value={hps.undyed} suffix={suffix} />
        </span>
      </div>
    );
  }

  return <AlphaPriceText value={hps.alpha} suffix={suffix} />;
};
